import { z } from 'zod';
import { IsoDateTime, Uuid } from './primitives.js';
/** Framer project ids are 20 url-safe characters; the same shape `framer-api` accepts. */
const PROJECT_KEY = /^[A-Za-z0-9]{20}$/u;
/** Share urls look like `/projects/Fuel-copy--aabbccdd1122eeff3344`. */
const SLUG_SUFFIX = /^.+--([A-Za-z0-9]+)/u;
/**
 * Accepts a project url or a bare project id and returns the id `framer-api` connects with.
 * Mirrors the library's own parser so we reject bad input before spending a connection.
 */
export function parseFramerProjectKey(input) {
    const value = input.trim();
    if (PROJECT_KEY.test(value))
        return value;
    try {
        const segments = new URL(value, 'https://framer.com').pathname.split('/').filter(Boolean);
        const index = segments.findIndex((segment) => segment.toLowerCase() === 'projects');
        if (index < 0)
            return null;
        const slug = segments[index + 1];
        if (slug === undefined)
            return null;
        const decoded = decodeURIComponent(slug);
        const candidate = decoded.match(SLUG_SUFFIX)?.[1] ?? decoded;
        return PROJECT_KEY.test(candidate) ? candidate : null;
    }
    catch {
        return null;
    }
}
export const FramerProjectUrl = z
    .string()
    .trim()
    .min(1)
    .max(2048)
    .refine((value) => parseFramerProjectKey(value) !== null, {
    message: 'Paste the Framer project URL (framer.com/projects/…) or the project id.',
});
/**
 * Documented as `fr_…`, but the prefix is not enforced: Framer changing its token format
 * must not lock users out. The real check is the connection test.
 */
export const FramerApiKey = z
    .string()
    .trim()
    .min(8)
    .max(512)
    .regex(/^[A-Za-z0-9._-]+$/u, { message: 'API key contains unexpected characters.' });
export const CreateProjectRequest = z.object({
    name: z.string().trim().min(1).max(120),
    framerUrl: FramerProjectUrl,
    apiKey: FramerApiKey,
    /**
     * The connect form's ownership declaration (AC-S2). A literal `true` rather than a boolean, so a
     * client that forgets it — or sends `false` — gets a `VALIDATION` error instead of a scan.
     */
    ownership: z.literal(true, {
        message: 'Confirm you own or are authorized to convert this Framer project.',
    }),
});
/** Omitting the key re-tests the stored one; sending a key replaces it (no partial edit). */
export const TestConnectionRequest = z.object({
    apiKey: FramerApiKey.optional(),
});
/** Everything the dashboard may see. The key itself never leaves the Worker. */
export const ProjectView = z.object({
    id: Uuid,
    name: z.string(),
    framerUrl: z.string(),
    framerProjectKey: z.string().nullable(),
    apiKeyLast4: z.string().length(4),
    lastScanAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const ProjectListResponse = z.object({
    items: z.array(ProjectView),
});
export const TestConnectionResponse = z.object({
    ok: z.literal(true),
    projectName: z.string().nullable(),
});
//# sourceMappingURL=projects.js.map